import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { RowData } from 'src/app/models/collection.interface';

@Component({
  selector: 'app-progress-cell',
  template: `
    <ng-container *ngIf="params?.data as d">
      <div class="progress-bar-container">
        <mat-progress-bar
          *ngIf="displayProgressBar(d)"
          mode="indeterminate"
          color="accent"
          style="width: 100%; height: 50px;">
        </mat-progress-bar>
      </div>

      <!-- Failed state -->
      <span *ngIf="!displayProgressBar(d) && isFailed(d)">
        <span
          [matTooltip]="failedTooltipText(d)"
          matTooltipClass="failed-tooltip"
          matTooltipPosition="right"
          matTooltipShowDelay="150"
          style="
        color: var(--mdc-theme-error, #d32f2f);
        text-decoration: underline;
        text-underline-offset: 2px;
        text-decoration-thickness: 1px;
      ">
          {{ failureLabel(d) }}
        </span>
      </span>

      <span *ngIf="!displayProgressBar(d) && !isFailed(d)">
        {{ params.value }}
      </span>
    </ng-container>
  `
})
export class ProgressCellComponent implements ICellRendererAngularComp {
  params!: ICellRendererParams;

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  refresh(): boolean {
    return false;
  }

  private ingestingStages = new Set(['started', 'image_extraction', 'text_extraction']);

  displayProgressBar(data: RowData): boolean {
    // Define the ingestion stages that should show the progress bar
    const stage = (data.processing_stage ?? '').trim().toLowerCase();

    // Hide the progress bar if the data is null, undefined, or white spaced
    if (!stage) return false;

    // Show the progress bar only if the processing stage matches one of the ingestingStages values
    return this.ingestingStages.has(stage);
  }

  isFailed(data: RowData): boolean {
    return (data?.processing_stage ?? '').trim().toLowerCase() === 'failed';
  }

  failedTooltipText(d: RowData): string {
    const name = this.displayName(d);
    return `Failed to ingest ${name}. Please reach out to an admin.`;
  }

  private filenameFromFileId(fid?: string): string {
    return fid?.includes('#') ? fid.split('#')[1] : (fid ?? '');
  }
  private filenameFromUri(uri?: string): string {
    if (!uri) return '';
    const clean = uri.split('?')[0].split('#')[0];
    try {
      return decodeURIComponent(clean.split('/').pop() || '');
    } catch {
      return clean.split('/').pop() || '';
    }
  }

  failureLabel(data: RowData): string {
    const name =
      this.filenameFromFileId(data?.file_id) ||
      this.filenameFromUri(data?.original_document_uri) ||
      (this.params?.value as string) ||
      'Document';
    return `${name} - failed`;
  }

  private displayName(d: RowData): string {
    const title = (this.params?.value as string) || '';
    if (title.trim()) return title;

    const byId = this.filenameFromFileId(d?.file_id);
    if (byId) return byId;

    const byUri = this.filenameFromUri(d?.original_document_uri);
    return byUri || 'Document';
  }
}
